import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WizardService } from 'src/app/wizard-bar/services/wizard.service';
import { forkJoin, Observable, of, Subject, Subscription } from 'rxjs';
import { QuotingModuleActionTypes, SetQuote, SetUtilityAccounts } from '@quoting/state/actions/quoting-module.actions';
import { Store } from '@ngrx/store';
import { QuoteRequestService } from '@app/shared/services/quote-request.service';
import { QuoteRequestState } from '@app/shared';
import { QuoteService } from '@app/quoting/services/quote.service';
import { UtilityHubIdentity } from '@auth/models/utility-hub-identity';
import { PricingSource, PricingSourceService } from '@app/shared/services/pricing-source.service';
import { QuoteRequestType } from '@app/shared/models/QuoteRequestType';
import { QuoteRequestSummary } from '@app/quoting/models/quote-request-summary';
import { UtilityInformationService } from '@app/shared/services/utility-information.service';
import { UdFile } from '@app/filemanagement/UdFile';
import { convertContractToMarginTerm, convertPriceToMarginTerm, removeDuplicatePrimitives, sortAscendingByProperty } from '@app/shared/helpers/array-helper';
import { ContractLength } from '@app/shared/models/contract-length';
import { CommodityType } from '@app/shared/enums/commodity-type';
import { Offer } from '@app/quoting/models/quote';
import { CustomService } from '@app/custom/services/custom.service';
import { QuoteRequestStatus } from '../../../shared/models/quote-request-status';
import { AppState } from '@app/state/app.state';
import { isReadOnlySelector, loggedInUtilityHubIdentitySelector, quoteSelector } from '@app/state/app.selectors';
import { SetSweetSpotCheckBoxState, SetPageName, SetProductType, SetCommodityType, SetContractLengthsSelected, SetMarginTerm, SetQuoteRequestType, SetPricesFilters, SetGetQuoteContractLengthStartingButtons, SetShowShareQuoteButton, SetUtilityUOM, SetSweetSpotWasRequested } from '@utility-information/state/actions/utility-information.actions';

@Component({
  selector: 'app-custom-get-quote',
  templateUrl: './custom-get-quote.component.html',
  styleUrls: ['./custom-get-quote.component.scss'],
})
export class CustomGetQuoteComponent implements OnInit, OnDestroy {
  public quoteRequestId: number;
  public generatingLoadProfileData: boolean;
  public submitSubject: Subject<void> = new Subject<void>();
  storeQuoteRequestId: number;
  quoteRequestState: QuoteRequestState;
  quote: any;
  public quoteRequestSummary: QuoteRequestSummary;

  @Input() pricingMethod: string;

  uhIdentity$: Subscription;
  route$: Subscription;
  load$: Subscription;
  selectQuote$: Subscription;
  loadProfile$: any;
  user: UtilityHubIdentity;
  file: UdFile;
  rateCodes: string[];
  isReadOnly$: Subscription;
  isReadOnly: boolean;

  constructor(
    private quoteRequestService: QuoteRequestService,
    private route: ActivatedRoute,
    private wizardService: WizardService,
    private store: Store<AppState>,
    private quoteService: QuoteService,
    private quoteCustomService: CustomService,
    private pricingSourceService: PricingSourceService,
    private utilityInformationService: UtilityInformationService,
  ) {}

  ngOnDestroy() {
    this.uhIdentity$?.unsubscribe();
    this.route$?.unsubscribe();
    this.load$?.unsubscribe();
    this.selectQuote$?.unsubscribe();
    this.loadProfile$?.unsubscribe();
    this.isReadOnly$?.unsubscribe();
  }

  ngOnInit(): void {
    this.wizardService.bindStep(this.route, () => this.save());
    this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId?.toString(), true));
    this.store.dispatch(new SetPageName(this.quoteRequestId?.toString(), 'getquote'));
    this.route$ = this.route.params.subscribe((x) => {
      if (!isNaN(x.quoteRequestId)) {
        this.quoteRequestId = Number(x.quoteRequestId);
        if (this.quoteRequestId !== this.storeQuoteRequestId) {
          this.load();
        }
      }
    });
    this.isReadOnly$ = this.store.select(isReadOnlySelector).subscribe((isReadOnly) => {
      this.isReadOnly = isReadOnly;
    });
  }

  private load() {
    this.load$ = forkJoin({
      quoteRequestState: this.quoteRequestService.getQuoteRequestState(this.quoteRequestId),
      quote: this.quoteRequestService.getSavedQuote(this.quoteRequestId),
      meters: this.quoteRequestService.getUtilityAccounts(this.quoteRequestId),
      rateCodes: this.utilityInformationService.getUtilityRateCodes(this.quoteRequestId),
      formData: this.quoteCustomService.getCompanyDetails(this.quoteRequestId),
    }).subscribe((res) => {
      this.store.dispatch(new SetProductType(res.quoteRequestState.quoteRequestId.toString(), res.quote.planType));
      this.store.dispatch(new SetCommodityType(res.quoteRequestState.quoteRequestId.toString(), res.quote.commodity));
      if (this.isSupplierManagerManualEntry)
        this.quoteRequestService.getQuoteRequestSummary(this.quoteRequestId).subscribe(x => {
          this.quoteRequestSummary = x;
        });

      if (res.quote.prices?.length > 0) {
        this.store.dispatch(new SetContractLengthsSelected(this.quoteRequestId.toString(), res.quote.prices?.map((x) => ({ months: x.term, selected: true }) as ContractLength)));
        let marginTerm = convertPriceToMarginTerm(res.quote.prices as Offer[]);
        const savedMarginDollar = res.quote.marginDollar ?? 0;
        if (savedMarginDollar !== 0) {
          marginTerm = marginTerm.map(mt =>
            mt.isSweetSpot && mt.margin === 0 ? { ...mt, margin: savedMarginDollar } : mt
          );
        }
        this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), marginTerm));
      }
      else {
        this.store.dispatch(new SetContractLengthsSelected(this.quoteRequestId.toString(), res.formData.contractLengths.map(x => ({ months: x, selected: true }) as ContractLength)));
        let marginTerm = convertContractToMarginTerm(res.formData.contractLengths.map(x => ({ months: x, selected: true }) as ContractLength), res.quote.marginDollar)
        this.store.dispatch(new SetMarginTerm(this.quoteRequestId.toString(), marginTerm));
      }

      this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
        this.user = user;
        this.store.dispatch(new SetQuote(res.quote, user, res.quoteRequestState.hasIDontKnows));
        this.store.dispatch(new SetQuoteRequestType(this.quoteRequestId.toString(), QuoteRequestType.Custom));
        this.store.dispatch(new SetPricesFilters(this.quoteRequestId.toString(), removeDuplicatePrimitives([...res.quote?.prices?.map(price => price?.planType), 'Show All']), res.quote?.prices.length > 0 ? res.quote?.prices?.map(price => price.term) : res.formData.contractLengths, true));
        this.store.dispatch(new SetCommodityType(this.quoteRequestId.toString(), res.quoteRequestState.commodity));
        this.store.dispatch(new SetPageName(this.quoteRequestId.toString(), "getquote"));
        this.store.dispatch(new SetSweetSpotCheckBoxState(this.quoteRequestId.toString(), true));
        this.store.dispatch(new SetGetQuoteContractLengthStartingButtons(this.quoteRequestId.toString(), res.quote.prices?.map(x => ({ months: x.term, selected: true }) as ContractLength)));
        this.store.dispatch(new SetProductType(this.quoteRequestId.toString(), res.quote.productType));
        this.store.dispatch(new SetShowShareQuoteButton(true));
      }).unsubscribe();
      this.rateCodes = res.rateCodes.map(x => x.rateCode);
      this.file = res.meters[0].consumptionData.dataFile;
      this.quoteRequestState = res.quoteRequestState;
      this.selectQuote$ = this.store.select(quoteSelector).subscribe((x) => {
        this.quote = x;

        if (x.quote.quoteRequestState?.status == QuoteRequestStatus.QuoteRequested && x.quote?.prices?.length > 0) {
          this.wizardService.stepClicked(this.wizardService.currentStep, false);
        }
      });
      this.quoteRequestId = this.quoteRequestState.quoteRequestId;
      this.quoteService.quoteRequestStateUpdated.emit(res.quoteRequestState);
      let quote = res.quote as any;
      const offersWithNoResult = quote.prices?.filter((x) => x.noResult);
      let offersWithResults = quote.prices?.filter((x) => !x.noResult);
      offersWithResults = sortAscendingByProperty(offersWithResults, 'marginTerm$');

      const sortedOffers = (offersWithResults || []).concat(offersWithNoResult);
      quote = {
        ...quote,
        prices: offersWithResults,
        marginDollar: this.isGasQuote() ? quote.marginDollar / 100 : quote.marginDollar,
        upliftDollar: this.isGasQuote() ? quote.upliftDollar / 100 : quote.upliftDollar,
      };
      this.quoteService.quoteUpdated.emit(quote);
      this.store.dispatch(new SetUtilityUOM(this.quoteRequestId.toString(), quote.uom));
      this.store.dispatch(new SetSweetSpotWasRequested(this.quoteRequestId.toString(), res?.quoteRequestState?.sweetSpotCheckbox));
      this.store.dispatch(new SetUtilityAccounts(this.quoteRequestId, res.meters));

      this.uhIdentity$ = this.store.select(loggedInUtilityHubIdentitySelector).subscribe((user) => {
        this.quote = quote;
      });
      
    });
  }

  public save(): Observable<any> {
    return of(true);
  }

  public next() {
    this.wizardService.next();
  }

  public advanceToLastStep() {
    this.wizardService.advanceToLastStep();
  }

  public back() {
    this.wizardService.previous();
  }

  // Submits a manually entered quote
  public submitQuote() {
    this.submitSubject.next();
  }

  isGasQuote(): boolean {
    return this.quoteRequestState && this.quoteRequestState.commodity == CommodityType.Gas;
  }

  public onAcceptOrRejectOffer(actionTaken: QuotingModuleActionTypes) {
    // TODO this logic needs to move out into the get steps call on the backend where we can disable and enable active steps
    // We only want the user to proceed to the contract details screen next in the journey if they have accepted an offer, otherwise skip to the you're done page.
    if (actionTaken == QuotingModuleActionTypes.OfferAccept) {
      this.wizardService.next();
    } else {
      const youreDoneStep = this.wizardService.activeSteps.find((step) => step.slug === 'youre-done');
      this.wizardService.stepClicked(youreDoneStep, false);
    }
  }

  public isSupplierManagerManualEntry(): Observable<boolean> {
    return new Observable<boolean>((n) => {
      if (!this.quoteRequestState) {
        n.next(false);
      } else {
        this.pricingSourceService
          .getPricingSource(this.quoteRequestState.quoteRequestType, this.quoteRequestState.commodity)
          .subscribe((ps) => {
            n.next(this.user.isSupplierManagerOrSalesManager && this.quoteRequestState.quoteRequestType === QuoteRequestType.Custom && ps.source != PricingSource.Dynamic);
            n.complete();
          });
      }
    });
  }
}

